import React from 'react';
import { Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import { useParams } from 'react-router-dom';

import { getRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

function TaskSprintDailyUpdates(props) {
  const { sprintId } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [savingDate, setSavingDate] = React.useState('');
  const [sprint, setSprint] = React.useState(null);
  const [days, setDays] = React.useState([]);

  const loadData = React.useCallback(() => {
    if (!sprintId) return;
    setLoading(true);
    getRequest(`${GET_URL.sprints.api}${sprintId}/`, { daily_updates: 1 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const payload = response.data?.data || {};
          setSprint(payload.sprint || null);
          setDays(payload.days || []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [props, sprintId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const monthGroups = React.useMemo(() => {
    const grouped = {};
    (days || []).forEach((row) => {
      const dateObj = new Date(row.date);
      if (Number.isNaN(dateObj.getTime())) return;
      const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[monthKey]) grouped[monthKey] = [];
      grouped[monthKey].push(row);
    });
    return Object.entries(grouped).map(([monthKey, values]) => {
      const [year, month] = monthKey.split('-').map((x) => parseInt(x, 10));
      const sortedValues = [...values].sort((a, b) => a.date.localeCompare(b.date));
      const firstVisible = sortedValues[0];
      const lastVisible = sortedValues[sortedValues.length - 1];
      const firstVisibleDate = new Date(firstVisible.date);
      const startWeekday = firstVisibleDate.getDay(); // 0 = Sun
      const valuesByDate = {};
      values.forEach((v) => { valuesByDate[v.date] = v; });
      const monthCells = [];
      for (let i = 0; i < startWeekday; i += 1) {
        monthCells.push({ empty: true, key: `${monthKey}-pad-${i}` });
      }
      const startDayNum = parseInt(firstVisible.date.split('-')[2], 10);
      const endDayNum = parseInt(lastVisible.date.split('-')[2], 10);
      for (let dayNum = startDayNum; dayNum <= endDayNum; dayNum += 1) {
        const iso = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        if (valuesByDate[iso]) {
          monthCells.push({ empty: false, key: iso, row: valuesByDate[iso] });
        }
      }
      while (monthCells.length % 7 !== 0) {
        monthCells.push({ empty: true, key: `${monthKey}-tail-${monthCells.length}` });
      }
      return { monthKey, year, month, cells: monthCells };
    });
  }, [days]);

  const saveRow = (row) => {
    setSavingDate(row.date);
    patchRequest(
      `${GET_URL.sprints.api}${sprintId}/?save_daily_update=1`,
      { update_date: row.date, update_text: row.update_text || '' },
      props
    )
      .then((response) => {
        if (response && response.status === 200) {
          Swal.fire({ icon: 'success', title: 'Daily update saved', timer: 900, showConfirmButton: false });
        }
        setSavingDate('');
      })
      .catch(() => setSavingDate(''));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>Daily Task Updates</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {sprint ? `${sprint.name} (${sprint.from_date} to ${sprint.to_date})` : 'Loading sprint...'}
      </Typography>
      {(monthGroups || []).map((monthBlock) => (
        <Paper key={monthBlock.monthKey} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {new Date(monthBlock.year, monthBlock.month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
              <Grid item xs key={`${monthBlock.monthKey}-${dayName}`}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">{dayName}</Typography>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={1}>
            {monthBlock.cells.map((cell) => (
              <Grid item xs={12 / 7} key={cell.key}>
                {cell.empty ? (
                  <Box sx={{ minHeight: 136, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px dashed #e2e8f0' }} />
                ) : (
                  <Paper variant="outlined" sx={{ p: 1, minHeight: 136, borderRadius: 1.5 }}>
                    <Typography variant="caption" fontWeight={700}>{cell.row.date}</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={cell.row.update_text || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setDays((prev) => prev.map((d) => (d.date === cell.row.date ? { ...d, update_text: val } : d)));
                      }}
                      sx={{ mt: 0.8 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mt: 0.8 }}
                      disabled={loading || savingDate === cell.row.date}
                      onClick={() => saveRow(cell.row)}
                    >
                      Save
                    </Button>
                  </Paper>
                )}
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}
    </Box>
  );
}

export default TaskSprintDailyUpdates;
